import {
  AddressInput,
  DocActorCompanyOrIndividual,
  ProjectType,
  Salutation,
} from "../../../clients/graphqlTypes.js";
import type { CategoryFragment } from "../categories/useCategories.generated.js";
import type { CrmCreateFormPrefill } from "../crm/CrmCreateForm.js";
import { PendingDocShareProjectPrefill_ResultFragment } from "./PendingDocShareProjectPrefill.generated.js";

// The three client states a quick-accept draft can be in (design decision 4):
// an existing canonical CRM match, a deferred contact the flow will create
// only on final submission, or no usable client data at all.
export type PendingDocShareProjectDraftClient =
  | { kind: "matched"; crmOrganisationId: string; title: string }
  | {
      kind: "newContact";
      contactType: DocActorCompanyOrIndividual;
      companyName: string | null;
      firstname: string | null;
      familyname: string | null;
      salutation: Salutation | null;
      email: string | null;
    }
  | { kind: "none" };

// UI-independent project-creation draft derived from a pending-share prefill.
// Owned outside `QuickAcceptProjectModal` so the prefill rules stay pure and
// testable without the form.
export interface PendingDocShareProjectDraft {
  pendingDocShareId: string;
  title: string;
  type: ProjectType | null;
  allowedProjectTypes: ProjectType[];
  categoryIds: string[];
  buildingAddress: AddressInput | null;
  client: PendingDocShareProjectDraftClient;
  // The contact the visible CLIENT data forms, independent of a CRM match, so
  // the modal can offer "create a new contact" next to a matched one.
  contactCandidate: PendingDocShareProjectDraftNewContact | null;
  // Only set for a canonical CRM match. Deliberately no project count: no
  // service provides one, and a raw count query is not added for it.
  matchedContactDetails: {
    createdAt: string | null;
  } | null;
  quoteSummary: {
    title: string;
    number: string;
    date: string;
    netTotal: number;
    sharingOrganisationTitle: string;
  };
}

export type PendingDocShareProjectDraftNewContact = Extract<
  PendingDocShareProjectDraftClient,
  { kind: "newContact" }
>;

type PrefillClientActor = NonNullable<
  PendingDocShareProjectPrefill_ResultFragment["clientActor"]
>;

// A contact candidate is derived deterministically from the actor's stored
// `companyOrIndividual` -- never inferred from which name fields happen to be
// filled -- and only ever from the visible fields already projected onto the
// actor by the backend.
function resolveContactCandidate(
  clientActor: PrefillClientActor | null | undefined
): PendingDocShareProjectDraftNewContact | null {
  if (!clientActor) return null;

  if (clientActor.companyOrIndividual === "COMPANY") {
    const companyName = clientActor.companyName?.trim();
    if (!companyName) return null;

    return {
      kind: "newContact",
      contactType: "COMPANY",
      companyName,
      firstname: null,
      familyname: null,
      salutation: null,
      email: clientActor.email ?? null,
    };
  }

  const firstname = clientActor.firstname?.trim();
  const familyname = clientActor.familyname?.trim();
  const salutation = clientActor.salutation;
  if (!firstname || !familyname || !salutation) return null;

  return {
    kind: "newContact",
    contactType: "INDIVIDUAL",
    companyName: null,
    firstname,
    familyname,
    salutation,
    email: clientActor.email ?? null,
  };
}

// A canonical match wins; otherwise the deferred candidate; otherwise none.
function resolveClient(
  clientActor: PrefillClientActor | null | undefined,
  contactCandidate: PendingDocShareProjectDraftNewContact | null
): PendingDocShareProjectDraftClient {
  if (clientActor?.matchedCrmCompanyId) {
    return {
      kind: "matched",
      crmOrganisationId: clientActor.matchedCrmCompanyId,
      title: clientActor.matchedCrmCompanyTitle ?? "",
    };
  }

  return contactCandidate ?? { kind: "none" };
}

// Matched CRM title when a match exists (even with empty visible fields),
// else the visible company name, else `firstname familyname`.
function resolveClientDisplayName(
  clientActor: PrefillClientActor | null | undefined
): string | null {
  if (!clientActor) return null;

  if (clientActor.matchedCrmCompanyTitle) {
    return clientActor.matchedCrmCompanyTitle;
  }

  const companyName = clientActor.companyName?.trim();
  if (companyName) return companyName;

  const fullName = [clientActor.firstname, clientActor.familyname]
    .filter(Boolean)
    .join(" ")
    .trim();
  return fullName || null;
}

export function mapPendingDocShareProjectPrefillToDraft(
  prefill: PendingDocShareProjectPrefill_ResultFragment
): PendingDocShareProjectDraft {
  const displayName = resolveClientDisplayName(prefill.clientActor);
  const contactCandidate = resolveContactCandidate(prefill.clientActor);

  return {
    pendingDocShareId: prefill.id,
    title: displayName
      ? `${displayName} - ${prefill.docTitle}`
      : prefill.docTitle,
    // Zero values -> null; exactly one -> preselected and still editable;
    // multiple -> null and the choices restrict to the quote's values.
    type: prefill.projectTypes.length === 1 ? prefill.projectTypes[0] : null,
    allowedProjectTypes:
      prefill.projectTypes.length > 1 ? prefill.projectTypes : [],
    categoryIds: prefill.categories,
    buildingAddress: prefill.buildingAddress
      ? {
          city: prefill.buildingAddress.city,
          postalCode: prefill.buildingAddress.postalCode,
          countryCode: prefill.buildingAddress.countryCode,
          streetLines1: prefill.buildingAddress.streetLines1,
          location: prefill.buildingAddress.location
            ? {
                lat: prefill.buildingAddress.location.lat,
                lng: prefill.buildingAddress.location.lng,
              }
            : null,
        }
      : null,
    client: resolveClient(prefill.clientActor, contactCandidate),
    contactCandidate,
    matchedContactDetails: prefill.clientActor?.matchedCrmCompanyId
      ? {
          createdAt: prefill.clientActor.matchedCrmCompanyCreatedAt ?? null,
        }
      : null,
    quoteSummary: {
      title: prefill.docTitle,
      number: prefill.quoteNumber,
      date: prefill.quoteDate,
      netTotal: prefill.quoteNetTotal,
      sharingOrganisationTitle: prefill.sharingOrganisationTitle,
    },
  };
}

// The quick modal shows the trade as one vertical (a root category); every
// other copied source-project category -- further roots, sub-categories, and
// ids the category list does not know -- is a further category, editable in
// the Advanced settings section, so changing the vertical replaces only the
// displayed root.
export function splitDraftCategories(
  categoryIds: string[],
  categories: CategoryFragment[]
): { vertical: string | null; furtherCategoryIds: string[] } {
  const byId = new Map(categories.map(c => [c.id, c]));
  const roots = categoryIds
    .map(id => byId.get(id))
    .filter((c): c is CategoryFragment => c !== undefined && !c.parentId)
    .sort((a, b) => a.rank - b.rank);

  const vertical = roots[0]?.id ?? null;

  return {
    vertical,
    furtherCategoryIds: categoryIds.filter(id => id !== vertical),
  };
}

// De-duplicates, so a further list that also holds the vertical submits it once.
export function recombineDraftCategories(
  vertical: string | null,
  furtherCategoryIds: string[]
): string[] {
  return Array.from(
    new Set(vertical ? [vertical, ...furtherCategoryIds] : furtherCategoryIds)
  );
}

export function contactCandidateDisplayName(
  candidate: PendingDocShareProjectDraftNewContact
): string {
  return (
    candidate.companyName ??
    [candidate.firstname, candidate.familyname].filter(Boolean).join(" ")
  );
}

// A deferred contact candidate as client-create form prefill, so the user can
// adjust it (e.g. drop a rejected email) before saving it as a real contact.
export function newContactToCrmCreateFormPrefill(
  client: PendingDocShareProjectDraftNewContact
): CrmCreateFormPrefill {
  return {
    contactType: client.contactType,
    companyName: client.companyName ?? undefined,
    firstname: client.firstname ?? undefined,
    familyname: client.familyname ?? undefined,
    salutation: client.salutation ?? undefined,
    email: client.email ?? undefined,
  };
}
